import { useState } from "react";
import { FilePenLine, Loader2, Trash2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { cn } from "@/shared/lib/utils";
import { relativeTime } from "../lib/relativeTime";

export interface MediaDraftCardProps {
  name: string;
  /** 首帧/封面 presigned URL;无(海报草稿压根没有这个字段)则走渐变 + icon 占位。 */
  coverUrl?: string | null;
  /** 占位图标(视频=Film、海报=Image)。 */
  placeholderIcon: LucideIcon;
  /** 占位渐变(按 draftId 稳定取色);省略走默认灰。 */
  placeholderGradient?: string;
  /** 副信息行,例如海报的「第 3/6 步」;省略则只显示「点继续编辑」。 */
  hint?: string;
  /**
   * 背后的异步作业还在跑(长图智能编排 / 视频编排)—— 封面右上挂「编排中」角标,提示行换转圈。
   * ★1005:编排要几分钟,之前只有一条 20 秒就消失的 toast,之后卡和普通草稿一模一样。
   */
  busy?: boolean;
  updatedAt?: string | null;
  /**
   * 这份草稿已经提交出片 —— 点它去看那一单(没提交过则省略)。
   *
   * ★这里标的是**出片途中**那段窗口,不是「永远留着」:后端 `MediaCompletionService`
   *   在成片**真正成功**时会软删来源草稿,失败/部分失败才保留(留着让人改提示词重渲或整单重试)。
   *   而视频那段窗口很长(编排实测 772s,出片还要几分钟),草稿箱这段时间一声不吭,
   *   用户就以为没提交上 —— 0813 爱民「视频已经在制作了,但是还在草稿箱里」。
   *   所以文案要说清的是「在做了 + 做成了会自己消失」,不是「它会一直在」。
   */
  onGoProduced?: () => void;
  onOpen: () => void;
  onDelete: (e: React.MouseEvent) => void;
  className?: string;
}

/**
 * 草稿卡(2026-07-16 三线布局统一 —— 用户原话「视频的草稿卡片比较好看」)。
 *
 * <p>规格取自原 VideoDraftCard:固定 16:9 封面框 + 左上「草稿」角标 + 标题 + 提示 + 时间 + 删除。
 * 海报此前是一条 <b>细条</b>(DraftRow),与下面的大卡明显不协调;现在三线同规格。
 *
 * <p><b>海报草稿没有封面</b>(PosterDraftDto 只有 draftId/name/currentStep/formJson/时间,后端压根不存首帧)
 * → 走渐变 + icon 占位。这跟视频「首帧还没出」时是同一条路径,视觉上一致。
 *
 * <p>封面 URL 可能 404(presign 有效但对象被生命周期清理)→ onError 回退占位,不留破图。
 * alt 一律空(decorative),破图时不会把 alt 文本渲染出来(修过的生产 bug)。
 */
export function MediaDraftCard({
  name,
  coverUrl,
  placeholderIcon: PlaceholderIcon,
  placeholderGradient,
  hint,
  busy,
  updatedAt,
  onGoProduced,
  onOpen,
  onDelete,
  className,
}: MediaDraftCardProps) {
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = !!coverUrl && !coverFailed;
  const title = name || "未命名草稿";

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "group flex flex-col overflow-hidden rounded-lg border border-primary/30 bg-primary/[0.03] text-left transition hover:border-primary/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        className
      )}
    >
      <div
        className="relative flex aspect-video w-full items-center justify-center overflow-hidden bg-muted"
        style={placeholderGradient ? { background: placeholderGradient } : undefined}
      >
        {showCover ? (
          <>
            {/* 模糊铺底填满,避免竖版/方形封面两侧大片空白 */}
            <img
              src={coverUrl}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-xl"
            />
            <img
              src={coverUrl}
              alt=""
              loading="lazy"
              onError={() => setCoverFailed(true)}
              className="relative z-10 h-full w-full object-contain"
            />
          </>
        ) : (
          <PlaceholderIcon
            className={cn(
              "h-7 w-7",
              placeholderGradient ? "text-white/85" : "text-muted-foreground/60"
            )}
          />
        )}
        <Badge className="absolute left-2 top-2 z-20 bg-black/55 text-2xs text-white backdrop-blur-sm">
          草稿
        </Badge>
        {busy && !onGoProduced && (
          <Badge
            data-testid="draft-busy-badge"
            className="absolute right-2 top-2 z-20 gap-1 bg-primary/85 text-2xs text-white backdrop-blur-sm"
          >
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            编排中
          </Badge>
        )}
        {onGoProduced && (
          <Badge
            data-testid="draft-consumed-badge"
            className="absolute right-2 top-2 z-20 bg-success/85 text-2xs text-white backdrop-blur-sm"
          >
            出片中
          </Badge>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <div className="flex items-start justify-between gap-2">
          <span className="line-clamp-1 text-sm font-medium">{title}</span>
          {/*
            外层已是 <button>,删除只能用 role="button" 的 <span> —— button 套 button 是非法 HTML,
            浏览器会把内层甩出去重排。onClick 里由调用方 stopPropagation(否则连带触发 onOpen)。
          */}
          <span
            role="button"
            tabIndex={0}
            aria-label={`删除草稿 ${title}`}
            onClick={onDelete}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onDelete(e as unknown as React.MouseEvent);
              }
            }}
            className="-mr-1 -mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </span>
        </div>
        <div className="flex items-center gap-1 text-2xs text-primary">
          {busy ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
          ) : (
            <FilePenLine className="h-3 w-3 shrink-0" />
          )}
          <span className="truncate" role={busy ? "status" : undefined}>
            {hint ?? "点继续编辑"}
          </span>
        </div>
        {/* ★这句要把「为什么它还在这儿」说完:只标一个角标会变成新的困惑
            ——「既然在做了为什么还躺在草稿箱」。所以连「会自己消失」带去处一起给。 */}
        {onGoProduced && (
          <span
            role="button"
            tabIndex={0}
            data-testid="draft-consumed-link"
            onClick={(e) => {
              e.stopPropagation();
              onGoProduced();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onGoProduced();
              }
            }}
            className="text-left text-2xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            这稿已经在出片了 —— 成片成功后会自动从草稿箱清掉,失败才留着给你重试。看进度 →
          </span>
        )}
        <div className="text-2xs text-muted-foreground">
          {/* 2026-07-16:绝对日期 → 相对时间(与三线任务卡同口径,共用 relativeTime)。 */}
          {updatedAt ? relativeTime(updatedAt) : "草稿"}
        </div>
      </div>
    </button>
  );
}
